import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/*
    🔥 CONTROL DE CAMBIOS SIN GUARDAR

    Compara el formulario actual contra el original y avisa si hay
    diferencias.

        const bar = useSaveBar({ initial: registro, values: form, onSave });

        <SaveBar bar={bar} />

    ⚠️ LA COMPARACIÓN ES POR CONTENIDO, NO POR REFERENCIA

    Con === , cualquier setState marcaría el formulario como sucio
    aunque el usuario haya escrito y borrado la misma letra. Se
    comparan los valores serializados, con las claves ordenadas para
    que { a, b } y { b, a } no den distinto.

    ⚠️ EL AVISO AL CERRAR LA PESTAÑA NO ES PERSONALIZABLE

    Desde 2011 los navegadores ignoran el texto de beforeunload y
    muestran el suyo: era un vector de phishing. Solo se puede decidir
    SI aparece.

    ⚠️ Ctrl/Cmd + S GUARDA

    Es el reflejo de cualquiera que edite algo. Sin capturarlo, el
    navegador abre "guardar página como", que no es lo que nadie
    quiso.
*/

// 🔥 Serializa ordenando claves: el orden del objeto no debería ensuciar nada
const onStable = (value) => {

    if (value === null || value === undefined) return 'null';

    if (Array.isArray(value)) return `[${value.map(onStable).join(',')}]`;

    if (value instanceof Date) return `d${value.getTime()}`;

    if (typeof value === 'object') {
        return `{${Object.keys(value).sort().map((k) => `${k}:${onStable(value[k])}`).join(',')}}`;
    }

    return String(value);
};

export const useSaveBar = ({
    initial = null,
    values = null,
    onSave = null,
    onDiscard = null,
    hasUnloadGuard = true,
    hasHotkey = true,
} = {}) => {

    const [useIsSaving, setIsSaving] = useState(false);
    const [useError, setError] = useState(null);
    const [useSavedAt, setSavedAt] = useState(null);

    // 🔥 El "original" se puede reemplazar después de guardar
    const REF__useRefBaseline = useRef(onStable(initial));

    const MEMO__IS_DIRTY = useMemo(
        () => onStable(values) !== REF__useRefBaseline.current,
        [values],
    );

    const CB__onCommit = useCallback(async () => {

        if (!MEMO__IS_DIRTY || useIsSaving) return false;

        setIsSaving(true);
        setError(null);

        try {

            await onSave?.(values);

            // 🔥 Lo guardado pasa a ser el nuevo original: la barra se va sola
            REF__useRefBaseline.current = onStable(values);

            setSavedAt(new Date());

            return true;

        } catch (err) {
            setError(err?.message ?? 'No pudimos guardar los cambios.');
            return false;
        } finally {
            setIsSaving(false);
        }

    }, [MEMO__IS_DIRTY, useIsSaving, onSave, values]);

    const CB__onRevert = useCallback(() => {
        setError(null);
        onDiscard?.();
    }, [onDiscard]);

    // 🔥 Para cuando el padre recarga el registro desde el backend
    const CB__onResetBaseline = useCallback((next) => {
        REF__useRefBaseline.current = onStable(next ?? values);
        setError(null);
    }, [values]);

    // -------------------- 🎯 AVISO AL CERRAR
    useEffect(() => {

        if (!hasUnloadGuard || !MEMO__IS_DIRTY) return;

        const onBeforeUnload = (event) => {
            event.preventDefault();
            // ⚠️ El texto lo ignoran todos los navegadores desde 2011.
            //    Devolver algo sigue siendo necesario en los viejos.
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);

    }, [hasUnloadGuard, MEMO__IS_DIRTY]);

    // -------------------- 🎯 ATAJO
    useEffect(() => {

        if (!hasHotkey) return;

        const onKeyDown = (event) => {

            if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 's') return;

            event.preventDefault();
            CB__onCommit();
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);

    }, [hasHotkey, CB__onCommit]);

    return {
        isDirty: MEMO__IS_DIRTY,
        isSaving: useIsSaving,
        error: useError,
        savedAt: useSavedAt,
        onSave: CB__onCommit,
        onDiscard: CB__onRevert,
        onResetBaseline: CB__onResetBaseline,
        setError,
    };
};
